import React from "react";
import {
  Modal,
  Box,
  IconButton,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

// Popup that shows clearly what is wrong in the uploaded Excel file
const UploadErrorModal = ({ open, details, onClose, onDownloadSample }) => {
  if (!details) return null;

  const missingColumns = details.missing_columns || [];
  const foundColumns = details.found_columns || [];
  const rowErrors = details.row_errors || [];
  const isWrongFormat = missingColumns.length > 0;

  const headCell = {
    fontWeight: "bold",
    backgroundColor: "#052660",
    color: "#fff",
    fontSize: "0.85rem",
    whiteSpace: "nowrap",
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "6px",
          boxShadow: "0px 3px 10px rgba(0, 0, 0, 0.15)",
          width: "720px",
          maxWidth: "94vw",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8, color: "#555" }}
        >
          <CloseIcon />
        </IconButton>

        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "20px 20px 8px" }}>
          <ErrorOutlineIcon sx={{ color: "#c62828" }} />
          <Typography variant="h6" sx={{ fontSize: "1.1rem" }}>
            Upload failed – nothing was saved
          </Typography>
        </Box>
        <Typography sx={{ px: "20px", color: "#555", fontSize: "0.9rem" }}>
          {isWrongFormat
            ? "This file does not have the columns needed for asset details."
            : `${rowErrors.length} row(s) need to be fixed. Please correct them in Excel and upload the file again.`}
        </Typography>

        {/* Body */}
        <Box sx={{ p: "16px 20px", overflowY: "auto", flex: 1 }}>
          {isWrongFormat ? (
            <>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", mb: 1 }}>
                Missing columns (row 1 of the Excel file must have these names):
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {missingColumns.map((c) => (
                  <Chip
                    key={c}
                    label={c}
                    size="small"
                    sx={{ backgroundColor: "#ffebee", color: "#c62828", fontWeight: 600 }}
                  />
                ))}
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", mb: 1 }}>
                Columns found in your file:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {foundColumns.length === 0 ? (
                  <Typography sx={{ fontSize: "0.85rem", color: "#888" }}>
                    No column names found in row 1
                  </Typography>
                ) : (
                  foundColumns.map((c) => <Chip key={c} label={c} size="small" />)
                )}
              </Box>
              <Typography sx={{ fontSize: "0.85rem", color: "#555" }}>
                Tip: download the <b>Sample Asset File</b>, fill your assets in it,
                and upload that file. Column names must stay exactly the same.
              </Typography>
            </>
          ) : (
            <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: "4px" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCell, width: "120px" }}>Excel row</TableCell>
                    <TableCell sx={headCell}>What is wrong</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowErrors.map((r, i) => (
                    <TableRow key={`${r.excel_row}-${i}`}>
                      <TableCell sx={{ fontWeight: 600, verticalAlign: "top" }}>
                        {r.excel_row ? `Row ${r.excel_row}` : "-"}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {(r.problems || []).map((p, j) => (
                          <div key={j} style={{ color: "#c62828", lineHeight: 1.6 }}>
                            • {p}
                          </div>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            p: "12px 20px 20px",
            borderTop: "1px solid #eee",
          }}
        >
          {onDownloadSample && (
            <Button variant="outlined" onClick={onDownloadSample}>
              Download Sample File
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ backgroundColor: "#052660" }}
          >
            OK
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UploadErrorModal;